/**
 * Detect a human-readable device name combining device model/OS with browser.
 * Uses the User-Agent Client Hints API when available for accurate model names
 * (e.g., "Pixel 8 - Chrome" instead of "Android Phone - Chrome"), then falls
 * back to UA string parsing.
 */
export async function getDeviceName(): Promise<string> {
  const ua = navigator.userAgent;

  // Detect browser (order matters: Edge/Opera use Chrome's engine)
  let browser = '';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  // Try Client Hints API for better device model (Chromium-based browsers)
  let model = '';
  try {
    const uaData = (navigator as any).userAgentData;
    if (uaData?.getHighEntropyValues) {
      const hints = await uaData.getHighEntropyValues(['model', 'platform']);
      if (hints.model && hints.model !== '' && hints.model !== 'K') {
        model = hints.model;
      }
    }
  } catch {
    // Client Hints not available, fall through to UA parsing
  }

  // Detect device/OS and combine with browser
  if (/iPhone/.test(ua)) return browser ? `iPhone - ${browser}` : 'iPhone';
  if (/iPad/.test(ua)) return browser ? `iPad - ${browser}` : 'iPad';
  if (/Android/.test(ua)) {
    const device = model || (/Mobile/.test(ua) ? 'Android Phone' : 'Android Tablet');
    return browser ? `${device} - ${browser}` : device;
  }
  if (/Mac OS X/.test(ua)) return browser ? `Mac - ${browser}` : 'Mac';
  if (/Windows/.test(ua)) return browser ? `Windows - ${browser}` : 'Windows PC';
  if (/CrOS/.test(ua)) return browser ? `Chromebook - ${browser}` : 'Chromebook';
  if (/Linux/.test(ua)) return browser ? `Linux - ${browser}` : 'Linux';

  return browser ? `${browser} Browser` : 'Unknown Device';
}
